/**
 * โคมแดง, the red lantern event (data/redLantern.ts, data/redLanternQuests.ts, docs/archive/QINGGONG_DROPS_RED_LANTERN.md
 * Part B). While the player is out in the new Yanyu, every `rollEvery` s of eligible time one draw of the World's Rng
 * may bring a red lantern 18-30 m away, 3-8 m above the ground. Touching it in the air opens it: a gift, or a note
 * (ใบโคม) naming an NPC who needs help, with one of four quests (deliver, find, help, escort); doing it earns an item
 * from that NPC and their regard. 8 a day, one quest open at a time, a lantern guaranteed after 30 minutes without one.
 *
 * Mode `redLantern` (npc | pvp): npc is personal. pvp asked with nobody found falls back to an NPC rival (a WB_SIM
 * name) who flies at the lantern at 0.85 x the player's sprint, so it is still a race; with players found the lantern is
 * shared (server side, not built). Quests stay personal. Reads the clock (`eventAt`, `dailyEventsAt`) and never writes
 * them. The client reads `redLanternOf(world)`.
 */
import { dailyEventsAt } from '../data/dailyEvents';
import { CRAFT_MATS } from '../data/itemCraft';
import { LANTERN_GIFTS, LANTERN_TABLE, RED_LANTERN as RL, type LanternGift, type LanternQuestKind } from '../data/redLantern';
import { JIUZHOU_CAMPS } from '../data/regions/jiuzhou.content';
import { LANTERN_CAMP_GIVERS, LANTERN_PAYS, LANTERN_QUESTS as Q } from '../data/redLanternQuests';
import { QG_ARTS, qgPageId } from '../data/qinggongDrops';
import { SHOP } from '../data/shop';
import { WORLD_CYCLE, eventAt } from '../data/worldEvents';
import { BAG_SIZE } from '../data/items';
import { guildFieldOrigin } from '../data/guildField';
import { simName, WORLD_BOSSES } from '../data/worldBosses';
import { floorAt, PX_METRES, Tile, tileAt } from '../map';
import { freeAt } from '../path';
import { coreOf, fromCore, toCore } from '../region/core';
import type { Entity, World } from '../world';
import { actRoleOf } from './npcActs';
import { addScore, memoryKey, memoryOf } from './npcMemory';
import { modesOf } from './modes';
import type { SystemHost, WorldSystem } from './types';

/** An open note. Positions are map coordinates. */
export interface LanternQuest {
  kind: LanternQuestKind;
  /** Help: what is asked (absent in old saves: the herb). */
  good?: string;
  n?: number;
  /** The giver's and (deliver) the receiver's names. */
  giver: string;
  receiver?: string;
  /** Seconds left before it expires. */
  t: number;
  phase: 'pickup' | 'carry' | 'seek' | 'return' | 'walk' | 'done';
  /** Seconds left to carry the parcel. */
  carry?: number;
  /** The find's real spot and its map circle's middle; the escort's destination (in x, y). */
  x?: number;
  y?: number;
  cx?: number;
  cy?: number;
  /** The escort's ambushers were set. */
  ambush?: boolean;
}

/** What the save keeps. */
export interface RedLanternSave {
  /** The arena day the count is for, lanterns opened that day, eligible seconds since the last lantern. */
  day: string;
  today: number;
  idle: number;
  quest?: LanternQuest;
  /** The camp-edge givers set down so far (core coordinates), put back after a reload. */
  camp?: { name: string; x: number; y: number }[];
}

const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
export function parseRedLantern(raw: unknown): RedLanternSave {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const q = r.quest as Record<string, unknown> | undefined;
  const kinds = ['deliver', 'find', 'help', 'escort'];
  const phases = ['pickup', 'carry', 'seek', 'return', 'walk', 'done'];
  const quest: LanternQuest | undefined =
    q && kinds.includes(q.kind as string) && phases.includes(q.phase as string) && typeof q.giver === 'string'
      ? {
          kind: q.kind as LanternQuestKind,
          giver: q.giver,
          ...(typeof q.receiver === 'string' ? { receiver: q.receiver } : {}),
          ...(typeof q.good === 'string' ? { good: q.good, n: num(q.n, 1) } : {}),
          t: num(q.t),
          phase: q.phase as LanternQuest['phase'],
          ...(q.carry !== undefined ? { carry: num(q.carry) } : {}),
          ...(q.x !== undefined ? { x: num(q.x), y: num(q.y) } : {}),
          ...(q.cx !== undefined ? { cx: num(q.cx), cy: num(q.cy) } : {}),
          ...(q.ambush === true ? { ambush: true } : {}),
        }
      : undefined;
  const campNames: readonly string[] = LANTERN_CAMP_GIVERS.map((g) => g.name);
  const camp = Array.isArray(r.camp)
    ? (r.camp as Record<string, unknown>[])
        .filter((c) => c && typeof c.name === 'string' && campNames.includes(c.name) && Number.isFinite(c.x) && Number.isFinite(c.y))
        .map((c) => ({ name: c.name as string, x: c.x as number, y: c.y as number }))
    : [];
  return {
    day: typeof r.day === 'string' ? r.day : '',
    today: Math.max(0, num(r.today)),
    idle: Math.max(0, num(r.idle)),
    ...(quest ? { quest } : {}),
    ...(camp.length ? { camp } : {}),
  };
}

interface Lantern {
  x: number;
  y: number;
  /** Height above the sea (m). */
  h: number;
  life: number;
  /** The NPC rival (pvp with nobody found): its name and seconds until it arrives. */
  rival?: { name: string; t: number };
}

// Shared across module copies (a dev server may serve this file under two URLs): the client reads it by world.
const SYSTEMS: WeakMap<object, RedLanternSystem> = ((globalThis as Record<symbol, unknown>)[Symbol.for('nine.redLantern')] ??=
  new WeakMap<object, RedLanternSystem>()) as WeakMap<object, RedLanternSystem>;
/** What the client draws: the lantern now, the quest open, the time left. */
export const redLanternOf = (w: World): { lantern: Readonly<Lantern> | null; quest: Readonly<LanternQuest> | null } => {
  const s = SYSTEMS.get(w);
  return { lantern: s?.lantern ?? null, quest: w.progress.redLantern?.quest ?? null };
};

export class RedLanternSystem implements WorldSystem {
  readonly name = 'redLantern';
  lantern: Lantern | null = null;
  /** The camp-edge villagers set down for the notes in a region (jiuzhou), by entity id. */
  private readonly campFolk = new Set<number>();
  private rollT: number = RL.rollEvery;
  private campRestored = false;

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  private eligible(): boolean {
    const w = this.h.world;
    return (
      RL.zones.includes(w.map.zone) &&
      !w.interiors.inside &&
      !w.duel &&
      !w.player.dead &&
      !w.scene &&
      w.progress.quest.index >= RL.minQuest &&
      !this.inBusyGround(w.player.x, w.player.y)
    );
  }

  /** The guild war's field and the world bosses' arenas (data in the town's metres): no lantern hangs there. */
  private inBusyGround(x: number, y: number): boolean {
    const w = this.h.world;
    const q = toCore(w.map, x, y);
    const f = guildFieldOrigin(w.map.zone);
    if (f && q.x >= f.x && q.x <= f.x + 120 && q.y >= f.y && q.y <= f.y + 90) return true;
    return WORLD_BOSSES.some((b) => Math.hypot(q.x - b.arena.x, q.y - b.arena.y) <= b.arena.r + RL.spawnMax);
  }

  private save(): RedLanternSave {
    const pr = this.h.world.progress;
    const s = (pr.redLantern ??= { day: '', today: 0, idle: 0 });
    if (s.day !== pr.arena.day) Object.assign(s, { day: pr.arena.day, today: 0 });
    return s;
  }

  step(dt: number): void {
    const w = this.h.world;
    if (!RL.zones.includes(w.map.zone)) return;
    const s = this.save();
    if (!this.campRestored) this.restoreCampFolk(s);
    if (s.quest) this.questStep(s, s.quest, dt);
    if (this.lantern) return this.watch(this.lantern, dt);
    if (!this.eligible()) return;
    s.idle += dt;
    if ((this.rollT -= dt) > 0 || s.today >= RL.perDay) return;
    this.rollT = RL.rollEvery;
    const phase = w.time % WORLD_CYCLE.length;
    const quiet = !eventAt(phase);
    const night = dailyEventsAt(w.map.zone, w.hour).some((d) => d.id === RL.nightEvent);
    const chance = RL.chance * (quiet ? RL.quietMul : 1) * (night ? RL.nightMul : 1);
    // One draw per roll, always taken (the guard decides after it).
    const r = this.h.random();
    if (r < chance || s.idle >= RL.guard) this.spawn();
  }

  private spawn(): void {
    const w = this.h.world;
    const p = w.player;
    for (let k = 0; k < 8; k++) {
      const a = this.h.random() * Math.PI * 2;
      const d = RL.spawnMin + this.h.random() * (RL.spawnMax - RL.spawnMin);
      const up = RL.hMin + this.h.random() * (RL.hMax - RL.hMin);
      const x = p.x + Math.cos(a) * d;
      const y = p.y + Math.sin(a) * d;
      if (x < 1 || y < 1 || x > w.map.w - 1 || y > w.map.h - 1 || !freeAt(w.map, x, y)) continue;
      if (tileAt(w.map, Math.floor(x), Math.floor(y)) === Tile.Water || w.inSafeZone({ x, y })) continue;
      const lantern: Lantern = { x, y, h: floorAt(w.map, x, y) * PX_METRES + up, life: RL.life };
      const modes = modesOf(w);
      if (modes && modes.pick('redLantern') === 'pvp' && modes.resolve('redLantern', undefined, true) === 'npc') {
        const name = simName(Math.floor(this.h.random() * 1000));
        lantern.rival = { name, t: RL.rival.from / Math.max(0.1, p.speed * RL.rival.speedMul * RL.rival.sprint) };
      }
      this.lantern = lantern;
      this.h.notice('โคมแดงลอยมาใกล้ท่าน');
      return;
    }
  }

  private watch(l: Lantern, dt: number): void {
    const w = this.h.world;
    const p = w.player;
    if (!this.eligible()) {
      this.lantern = null;
      return;
    }
    if ((l.life -= dt) <= 0) {
      this.lantern = null;
      this.h.notice('โคมแดงลอยสูงขึ้นและหายไป');
      return;
    }
    if (l.rival && (l.rival.t -= dt) <= 0) {
      this.h.notice(`${l.rival.name} ชิงโคมแดงไปก่อน`);
      this.lantern = null;
      return;
    }
    const alt = (floorAt(w.map, p.x, p.y) + p.z) * PX_METRES;
    if (p.z <= 0 || Math.hypot(p.x - l.x, p.y - l.y) > RL.reach || Math.abs(alt - l.h) > RL.band) return;
    this.lantern = null;
    this.open();
  }

  private pickWeighted(table: readonly { kind: string; w: number }[]): string {
    const total = table.reduce((a, e) => a + e.w, 0);
    let r = this.h.random() * total;
    for (const e of table) if ((r -= e.w) < 0) return e.kind;
    return table[table.length - 1]!.kind;
  }

  private open(): void {
    const w = this.h.world;
    const s = this.save();
    s.today++;
    s.idle = 0;
    let givers = s.quest ? [] : this.candidates();
    if (!s.quest && givers.length < 2 && this.seedCampFolk()) givers = this.candidates();
    const questsOk = givers.length >= 2;
    const kind = this.pickWeighted(LANTERN_TABLE.filter((e) => questsOk || !['deliver', 'find', 'help', 'escort'].includes(e.kind)));
    if (['deliver', 'find', 'help', 'escort'].includes(kind)) return this.startQuest(s, kind as LanternQuestKind, givers);
    this.gift(kind as LanternGift);
    this.h.emit({ type: 'progressChanged' });
    void w;
  }

  private gift(kind: LanternGift): void {
    const pr = this.h.world.progress;
    const G = LANTERN_GIFTS;
    const good = (id: string, n: number) => {
      pr.trades.goods[id] = (pr.trades.goods[id] ?? 0) + n;
    };
    const range = (a: readonly [number, number]) => a[0] + Math.floor(this.h.random() * (a[1] - a[0] + 1));
    if (kind === 'goods') {
      const id = G.goods.ids[Math.floor(this.h.random() * G.goods.ids.length)]!;
      const n = range(G.goods.n);
      good(id, n);
      return this.h.notice(`โคมแดงมอบวัตถุดิบ ${n}`);
    }
    if (kind === 'gem') {
      const id = this.pickWeighted(G.gems.map((e) => ({ kind: e.good, w: e.w })));
      good(id === 'reroll-stone' ? CRAFT_MATS.reroll : id, 1);
      return this.h.notice('โคมแดงมอบอัญมณี');
    }
    if (kind === 'gear' && pr.inventory.length < BAG_SIZE) {
      const e = SHOP[Math.floor(this.h.random() * SHOP.length)]!;
      pr.inventory.push(e.item);
      return this.h.notice('โคมแดงมอบอุปกรณ์หนึ่งชิ้น');
    }
    if (kind === 'page') {
      const arts = Object.keys(QG_ARTS).filter((a) => !pr.qinggong[a]);
      if (arts.length) {
        good(qgPageId(arts[Math.floor(this.h.random() * arts.length)]!), 1);
        return this.h.notice('โคมแดงมอบหน้าตำรา');
      }
    }
    // Purse (also the stand-in for a full bag, and for the page until its goods exist).
    const gold = Math.round(range(G.purse) * (1 + this.h.world.player.level / 10));
    pr.gold += gold;
    this.h.notice(`โคมแดงมอบเงิน ${gold} ตำลึง`);
  }

  // ───────── the notes ─────────

  private roleOf = (e: Entity) => actRoleOf(e) ?? 'villager';

  /** Townsfolk and act people out of doors who can give or receive a note. */
  private candidates(): Entity[] {
    const out: Entity[] = [];
    for (const e of this.h.world.entities.values()) {
      if (e.kind !== 'npc' || e.dead || e.folk?.indoors) continue;
      if (this.campFolk.has(e.id)) {
        out.push(e);
        continue;
      }
      if (!e.folk && !actRoleOf(e)) continue;
      if (!Q.givers.includes(this.roleOf(e)) || !memoryKey(this.h.world, e)) continue;
      out.push(e);
    }
    return out;
  }

  /** In a region with no townsfolk close by: two villagers at the edges of the nearest camps (data/redLanternQuests.ts). */
  private seedCampFolk(): boolean {
    const w = this.h.world;
    const o = coreOf(w.map);
    if (o.x === 0 && o.y === 0) return false;
    const p = w.player;
    const edge = ([x0, x1, y0, y1]: readonly [number, number, number, number]) => ({
      x: Math.max(x0 - 6, Math.min(x1 + 6, p.x)),
      y: Math.max(y0 - 6, Math.min(y1 + 6, p.y)),
    });
    const near = JIUZHOU_CAMPS.map((c) => ({ c, at: edge(c.rect) }))
      .filter((a) => freeAt(w.map, a.at.x, a.at.y))
      .sort((a, b) => Math.hypot(a.at.x - p.x, a.at.y - p.y) - Math.hypot(b.at.x - p.x, b.at.y - p.y))
      .slice(0, LANTERN_CAMP_GIVERS.length);
    const saved = (this.h.world.progress.redLantern ??= { day: '', today: 0, idle: 0 });
    saved.camp = [];
    near.forEach((a, k) => {
      const g = LANTERN_CAMP_GIVERS[k]!;
      saved.camp!.push({ name: g.name, ...toCore(w.map, a.at.x, a.at.y) });
      const e = this.h.add({ kind: 'npc', x: a.at.x, y: a.at.y, sprite: g.sprite, name: g.name, title: g.title, facing: 1 });
      this.campFolk.add(e.id);
    });
    return near.length >= 2;
  }

  /** After a reload: put the saved camp-edge givers back where they stood (once per session). */
  private restoreCampFolk(s: RedLanternSave): void {
    this.campRestored = true;
    const w = this.h.world;
    for (const c of s.camp ?? []) {
      const g = LANTERN_CAMP_GIVERS.find((x) => x.name === c.name);
      if (!g || this.byName(c.name)) continue;
      const at = fromCore(w.map, c.x, c.y);
      const e = this.h.add({ kind: 'npc', x: at.x, y: at.y, sprite: g.sprite, name: g.name, title: g.title, facing: 1 });
      this.campFolk.add(e.id);
    }
  }

  private byName(name: string): Entity | undefined {
    for (const e of this.h.world.entities.values()) if (e.kind === 'npc' && e.name === name && !e.dead) return e;
    return undefined;
  }

  private startQuest(s: RedLanternSave, kind: LanternQuestKind, givers: Entity[]): void {
    const w = this.h.world;
    const p = w.player;
    const g = givers[Math.floor(this.h.random() * givers.length)]!;
    const others = givers.filter((e) => e !== g && e.name !== g.name);
    const far = (c: { minDist: number; maxDist: number }, from: Entity) =>
      others.filter((e) => Math.hypot(e.x - from.x, e.y - from.y) >= c.minDist && Math.hypot(e.x - from.x, e.y - from.y) <= c.maxDist);
    const q: LanternQuest = { kind, giver: g.name, t: Q.expire, phase: 'pickup' };
    if (kind === 'deliver') {
      const pool = far(Q.deliver, g);
      const r = (pool.length ? pool : others).sort((a, b) => Math.hypot(b.x - g.x, b.y - g.y) - Math.hypot(a.x - g.x, a.y - g.y))[0];
      if (!r) return this.gift('purse');
      q.receiver = r.name;
    } else if (kind === 'find') {
      q.phase = 'seek';
      let ok = false;
      for (let k = 0; k < 12 && !ok; k++) {
        const a = this.h.random() * Math.PI * 2;
        const d = Q.find.minDist + this.h.random() * (Q.find.maxDist - Q.find.minDist);
        const x = p.x + Math.cos(a) * d;
        const y = p.y + Math.sin(a) * d;
        const oa = this.h.random() * Math.PI * 2;
        const od = this.h.random() * Q.find.circle * 0.6;
        if (x < 2 || y < 2 || x > w.map.w - 2 || y > w.map.h - 2 || !freeAt(w.map, x, y) || tileAt(w.map, Math.floor(x), Math.floor(y)) === Tile.Water) continue;
        Object.assign(q, { x, y, cx: x + Math.cos(oa) * od, cy: y + Math.sin(oa) * od });
        ok = true;
      }
      if (!ok) return this.gift('purse');
    } else if (kind === 'escort') {
      // Within the data's range when someone is, else the farthest soul in town.
      const pool = far(Q.escort, g);
      const r = pool.length
        ? pool[Math.floor(this.h.random() * pool.length)]
        : others.sort((a, b) => Math.hypot(b.x - g.x, b.y - g.y) - Math.hypot(a.x - g.x, a.y - g.y))[0];
      if (!r) return this.gift('purse');
      Object.assign(q, { x: r.x, y: r.y, receiver: r.name });
    }
    let text: string = Q.text[kind];
    if (kind === 'help') {
      const v = Q.helpBy[this.roleOf(g)] ?? { good: Q.help.good, n: Q.help.n, text };
      Object.assign(q, { good: v.good, n: v.n });
      text = v.text;
    }
    s.quest = q;
    this.h.notice(text.replace('{giver}', g.name).replace('{receiver}', q.receiver ?? '').replace('{n}', String(q.n ?? Q.help.n)));
    this.h.emit({ type: 'progressChanged' });
  }

  private questStep(s: RedLanternSave, q: LanternQuest, dt: number): void {
    const w = this.h.world;
    const p = w.player;
    if (w.map.zone !== 'yanyu2') return;
    if ((q.t -= dt) <= 0) {
      delete s.quest;
      return this.h.notice(`ใบโคมของ ${q.giver} หมดเวลา`);
    }
    const g = this.byName(q.giver);
    if (!g) return;
    const near = (e: { x: number; y: number }, r: number) => Math.hypot(e.x - p.x, e.y - p.y) <= r;
    if (q.kind === 'deliver') {
      if (q.phase === 'pickup' && near(g, Q.deliver.near)) {
        q.phase = 'carry';
        q.carry = Q.deliver.carry;
        this.h.notice(`รับพัสดุจาก ${g.name} ส่งให้ ${q.receiver}`);
      } else if (q.phase === 'carry') {
        if ((q.carry = (q.carry ?? 0) - dt) <= 0) {
          delete s.quest;
          return this.h.notice('ส่งพัสดุไม่ทันเวลา');
        }
        const r = this.byName(q.receiver ?? '');
        if (r && near(r, Q.deliver.near)) this.finish(s, r);
      }
    } else if (q.kind === 'find') {
      if (q.phase === 'seek' && q.x !== undefined && Math.hypot(q.x - p.x, q.y! - p.y) <= Q.find.near) {
        q.phase = 'return';
        this.h.notice(`พบของที่ ${g.name} ทำหาย นำกลับไปคืน`);
      } else if (q.phase === 'return' && near(g, Q.find.near)) this.finish(s, g);
    } else if (q.kind === 'help') {
      const good = q.good ?? Q.help.good;
      const need = q.n ?? Q.help.n;
      const have = w.progress.trades.goods[good] ?? 0;
      if (near(g, Q.help.near) && have >= need) {
        w.progress.trades.goods[good] = have - need;
        if (!w.progress.trades.goods[good]) delete w.progress.trades.goods[good];
        this.finish(s, g);
      }
    } else if (q.kind === 'escort') {
      if (q.phase === 'pickup' && near(g, Q.escort.near)) {
        q.phase = 'walk';
        this.h.notice(`พา ${g.name} ไปส่งที่จุดหมาย`);
      } else if (q.phase === 'walk') {
        // The giver walks after the player (a little slower), keeping 1.5 m behind.
        const gd = Math.hypot(p.x - g.x, p.y - g.y);
        if (gd > 1.5) {
          const step = Math.min(gd - 1.5, p.speed * 0.95 * dt);
          this.h.move(g, ((p.x - g.x) / gd) * step, ((p.y - g.y) / gd) * step);
        }
        if (!q.ambush && Math.hypot(q.x! - g.x, q.y! - g.y) < Math.hypot(q.x! - p.x, q.y! - p.y) + 60 && q.x !== undefined) {
          const mx = (g.x + q.x) / 2;
          const my = (g.y + q.y!) / 2;
          if (Math.hypot(mx - p.x, my - p.y) < 40) {
            q.ambush = true;
            for (let k = 0; k < Q.escort.ambushers; k++) this.h.spawnMonster(Q.escort.monster, mx + k * 2, my);
            this.h.notice('โจรดักทาง!');
          }
        }
        // Done when both the player and the giver are at the destination.
        if (Math.hypot(q.x! - p.x, q.y! - p.y) <= Q.escort.near && Math.hypot(q.x! - g.x, q.y! - g.y) <= Q.escort.near * 2) this.finish(s, g);
      }
    }
  }

  /** The pay of whoever the quest ends with: ตำลึง, their role's goods, their regard. */
  private finish(s: RedLanternSave, payer: Entity): void {
    const w = this.h.world;
    const pr = w.progress;
    delete s.quest;
    const gold = Q.gold[0] + Math.floor(this.h.random() * (Q.gold[1] - Q.gold[0] + 1));
    pr.gold += gold;
    const pay = LANTERN_PAYS[this.roleOf(payer)];
    let got = `${gold} ตำลึง`;
    if (pay) {
      const good = (id: string, n: number) => (pr.trades.goods[id] = (pr.trades.goods[id] ?? 0) + n);
      good(pay.good, pay.n);
      got += ` และวัตถุดิบ ${pay.n}`;
      if (pay.extra && this.h.random() < pay.extra.chance) {
        good(pay.extra.good, 1);
        got += ' กับของพิเศษ';
      }
    }
    const key = memoryKey(w, payer);
    if (key) addScore(memoryOf(pr).npc, key, Q.regard);
    this.h.notice(`${payer.name} ตอบแทน ${got}`);
    this.h.emit({ type: 'progressChanged' });
  }
}
